import React, { useEffect, useState } from 'react';
import useOnlyDevelopers from 'state/setOnlyDevelopers';
import { IMinDetails, IProductDelivery, IproductReferenceDataInCountry, IShipmentData } from '../../../../types/orders';
import { productHasActiveStockReservation } from 'utils/productStockReservation.utils';
import BordOneToneIcon from '../../../../components/NudosComponents/BordOneToneIcon/BordOneToneIcon';
import ProductSelectorList from './components/ProductSelectorList/ProductSelectorList';
import ProductSelectorHeader from './components/ProductSelectorHeader/ProductSelectorHeader';
import './ProductSelector.scss';

interface ProductSelectorProps {
  shippingChosenByDefault?: IShipmentData;
  listOfShipmentsAvailableToAdd?: IShipmentData[];
  setListOfShipmentsToChangeStatus?: React.Dispatch<React.SetStateAction<IShipmentData[]>>;
  errorList?: IMinDetails[];
  setErrorList?: React.Dispatch<React.SetStateAction<IMinDetails[] | undefined>>;
  hasTemporaryStorage?: boolean;
  customAlertMessage?: string | React.ReactNode;
  displayTemporaryStorageAlert?: boolean;
  serialHasAnyError: (hasErrors: boolean) => void;
  requireSerials?: boolean;
  headerTitle: string;
  references?: IproductReferenceDataInCountry[];
  isStockOrder?: boolean;
  listOfSelectedShipments?: IShipmentData[];
  shipmentNumberForLabel?: number;
  blockReservedStockSelection?: boolean;
}

const ProductSelector: React.FC<ProductSelectorProps> = ({
  listOfSelectedShipments = [],
  listOfShipmentsAvailableToAdd,
  shippingChosenByDefault,
  setListOfShipmentsToChangeStatus,
  errorList,
  setErrorList,
  customAlertMessage,
  displayTemporaryStorageAlert,
  hasTemporaryStorage,
  serialHasAnyError,
  requireSerials,
  headerTitle,
  references,
  isStockOrder,
  shipmentNumberForLabel,
  blockReservedStockSelection = false
}) => {
  const [selectAll, setSelectAll] = useState<boolean>(false);
  const [listOfSelectedItems, setListOfSelectedItems] = useState<IShipmentData[]>([]);
  const [listOfUnselectedItems, setListOfUnselectedItems] = useState<IShipmentData[]>([]);
  const [selectedProductIds, setSelectedProductIds] = useState<number[]>([]);
  const { isOnlyDevsLogistics } = useOnlyDevelopers();
  const shouldBlockReservedStockSelection = blockReservedStockSelection && isOnlyDevsLogistics;

  const displayDivider = listOfUnselectedItems?.length > 0;

  const selectedProducts =
    listOfSelectedItems?.flatMap(shipment =>
      shipment.products.filter(product => selectedProductIds.includes(product.id))
    ) || [];

  const getItemKey = (item: IShipmentData) => item.shipmentId;

  const findProductById = (productId: number): IProductDelivery | undefined =>
    [...listOfSelectedItems, ...listOfUnselectedItems].flatMap(s => s.products).find(p => p.id === productId);

  const isProductLockedByStockReservation = (product: IProductDelivery) =>
    shouldBlockReservedStockSelection &&
    productHasActiveStockReservation(product, references ?? []);

  const addToListToChangeStatus = (shipmentId: number) => {
    const item = listOfUnselectedItems?.find(i => getItemKey(i) === shipmentId);
    const removeFromTheUnSelectedList = listOfUnselectedItems?.filter(i => getItemKey(i) !== shipmentId);
    if (item) setListOfSelectedItems([...listOfSelectedItems, item]);
    setListOfUnselectedItems([...removeFromTheUnSelectedList]);
  };

  const removeToListToChangeStatus = (shipmentId: number) => {
    const item = listOfSelectedItems?.find(i => getItemKey(i) === shipmentId);
    const removeFromTheSelectedList = listOfSelectedItems?.filter(i => getItemKey(i) !== shipmentId);

    if (item) {
      const productIdsToRemove = item.products.map(product => product.id);
      setSelectedProductIds(prev => prev.filter(productId => !productIdsToRemove.includes(productId)));
      setListOfUnselectedItems([...listOfUnselectedItems, item]);
    }

    setListOfSelectedItems([...removeFromTheSelectedList]);
  };

  const cleanSelectedProductIds = () => {
    setSelectAll(false);
    setSelectedProductIds([]);
    setListOfUnselectedItems([...listOfUnselectedItems, ...listOfSelectedItems]);
    setListOfSelectedItems([]);
  };

  const handleSelectAllChange = () => {
    if (selectAll) return cleanSelectedProductIds();
    setSelectAll(true);
    const allProductIds = [...listOfSelectedItems, ...listOfUnselectedItems].flatMap(shipment =>
      shipment.products
        .filter(product => !isProductLockedByStockReservation(product))
        .map(product => product.id)
    );
    setSelectedProductIds(allProductIds);
    setListOfSelectedItems([...listOfSelectedItems, ...listOfUnselectedItems]);
    setListOfUnselectedItems([]);
  };

  const defaultSelectItem = () => {
    if (!!shippingChosenByDefault && listOfSelectedShipments?.length === 0) {
      setListOfSelectedItems([shippingChosenByDefault]);
    } else {
      setListOfSelectedItems(listOfSelectedShipments || []);
    }
  };

  const selectItemsList = () => {
    if (!listOfShipmentsAvailableToAdd) return;

    const available = listOfShipmentsAvailableToAdd.filter(
      item =>
        !listOfSelectedShipments?.some(
          selectedItem => getItemKey(selectedItem) === getItemKey(item)
        )
    );

    setListOfUnselectedItems(available);
  };

  /** Excluye SN con reserva al mover a Tránsito a bodega. */
  const toggleProductSelection = (productId: number) => {
    if (isStockOrder) return;
    const product = findProductById(productId);
    if (product && isProductLockedByStockReservation(product)) return;
    setSelectedProductIds(prev => {
      if (prev.includes(productId)) return prev.filter(id => id !== productId);
      return [...prev, productId];
    });
  };

  const getFilteredShipments = () => {
    if (!selectedProductIds.length) return [];
    return listOfSelectedItems
      ?.filter(shipment => shipment.products.some(product => selectedProductIds.includes(product.id)))
      ?.map(shipment => ({
        ...shipment,
        products: shipment.products.filter(product => selectedProductIds.includes(product.id))
      }));
  };

  /** UI: label «Serial de stock» e input precargado (independiente del bloqueo de selección). */
  const hasStockReservation = (product: IProductDelivery) => {
    if (isOnlyDevsLogistics && productHasActiveStockReservation(product, references ?? [])) return true;
    if (product?.stockReservation?.stockProduct?.serial) return true;
    return !!getStockSerial(product);
  };

  const getProductsSerial = (shipment: IShipmentData) => {
    return shipment?.products?.map(product => ({
      ...product,
      serial: product?.serial ?? ''
    }));
  };

  const getUpdatedListOfItemsToChangeStatus = () => {
    const listOfItemsToChangeStatus = getFilteredShipments();

    return listOfItemsToChangeStatus?.map(shipment => ({
      ...shipment,
      products: getProductsSerial(shipment)
    }));
  };

  const handleChangeSelectedItems = () => {
    const updatedListOfItemsToChangeStatus = getUpdatedListOfItemsToChangeStatus();
    setListOfShipmentsToChangeStatus?.(updatedListOfItemsToChangeStatus);
  };

  const editProductSerial = (productId: number, newSerial: string) => {
    const updatedShipments = listOfSelectedItems.map(shipment => ({
      ...shipment,
      products: shipment?.products?.map(product =>
        product?.id === productId ? { ...product, serial: newSerial } : product
      )
    }));
    setErrorList?.(undefined);
    setListOfSelectedItems([...updatedShipments]);
  };

  const getStockSerial = (product: IProductDelivery) => {
    const stockSerial = references
      ?.find(ref => ref.sku === product.sku)
      ?.items?.find(item => item.saleId === product.saleId)?.stockReservation?.stockProduct?.serial;
    return stockSerial || '';
  };

  const getStockReservationSerial = (product: IProductDelivery) => {
    if (product?.stockReservation?.stockProduct?.serial) return product.stockReservation.stockProduct.serial;
    return getStockSerial(product);
  };

  const getProductsWithoutStockSerial = (allSelectedProducts: IProductDelivery[]) => {
    return allSelectedProducts.filter(product => {
      const stockSerial = getStockSerial(product);
      return !stockSerial;
    });
  };

  const getDuplicateSerialInDatabase = (serial: string, errorList: IMinDetails[]) => {
    const duplicateSerialInDatabase = errorList?.find(
      error => String(error?.serial).trim().toLowerCase() === serial.trim().toLowerCase()
    );
    return duplicateSerialInDatabase ? 'Este serial ya está asignado a una herramienta' : undefined;
  };

  const getDuplicateSerialsInInputs = (targetSerial: string) => {
    const productsWithoutStockSerial = getProductsWithoutStockSerial(selectedProducts);
    const serials = productsWithoutStockSerial?.map(product => product?.serial?.trim()).filter(Boolean);
    const serialCount = serials.filter(serial => serial === targetSerial).length;
    return serialCount > 1 ? 'El serial está repetido' : undefined;
  };

  const getDuplicateSerials = (serial: string, errorList: IMinDetails[]) => {
    const duplicateSerialInDatabase = getDuplicateSerialInDatabase(serial, errorList || []);
    const duplicateSerialsInInputs = getDuplicateSerialsInInputs(serial);
    return !!duplicateSerialInDatabase ? duplicateSerialInDatabase : duplicateSerialsInInputs;
  };

  const getErrorText = (itemData: IProductDelivery) => {
    if (isStockOrder && !requireSerials) return undefined;
    const serial = itemData?.serial?.trim();
    if (!serial) return undefined;
    const duplicateSerials = getDuplicateSerials(serial, errorList || []);
    return duplicateSerials;
  };

  const hasProductsWithoutSerials = () => {
    return selectedProducts?.some(product => {
      const hasProductSerial = !!product?.serial?.trim();
      const hasStocKSerial = !!getStockReservationSerial(product)?.trim();
      return !hasProductSerial && !hasStocKSerial;
    });
  };

  const hasAnyDuplicateSerials = () => {
    const productsWithoutStockSerial = getProductsWithoutStockSerial(selectedProducts);
    const serials = productsWithoutStockSerial?.map(product => product?.serial?.trim()).filter(Boolean);
    const serialSet = new Set(serials);
    return serials.length !== serialSet.size;
  };

  const getSerialErrors = () => {
    if (isStockOrder && !requireSerials) return false;
    const hasLocalDuplicates = hasAnyDuplicateSerials();
    const hasApiErrors = !!errorList?.length;
    const hasMissingSerials = requireSerials && hasProductsWithoutSerials();
    return !!hasLocalDuplicates || !!hasApiErrors || !!hasMissingSerials;
  };

  const selectAllProductsWhenStockOrder = () => {
    if (!(isStockOrder && listOfSelectedItems.length > 0)) return;
    const allProductIds = listOfSelectedItems.flatMap(shipment => shipment.products.map(product => product.id));
    setSelectedProductIds(allProductIds);
  };

  const excludeReservedProductsFromSelection = () => {
    if (!shouldBlockReservedStockSelection) return;
    const reservedIds = listOfSelectedItems
      .flatMap(s => s.products)
      .filter(p => isProductLockedByStockReservation(p))
      .map(p => p.id);
    if (reservedIds.length === 0) return;
    setSelectedProductIds(prev => {
      const next = prev.filter(id => !reservedIds.includes(id));
      if (next.length === prev.length) return prev;
      return next;
    });
  };

  useEffect(() => {
    defaultSelectItem();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    selectItemsList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    excludeReservedProductsFromSelection();
  }, [listOfSelectedItems, references, shouldBlockReservedStockSelection]);

  useEffect(() => {
    handleChangeSelectedItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listOfSelectedItems]);

  useEffect(() => {
    handleChangeSelectedItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProductIds]);

  useEffect(() => {
    const serialErrors = getSerialErrors();
    serialHasAnyError(serialErrors);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listOfSelectedItems, errorList, selectedProductIds]);

  useEffect(() => {
    selectAllProductsWhenStockOrder();
  }, [isStockOrder, listOfSelectedItems]);

  return (
    <div className="productSelectorContainer">
      <ProductSelectorHeader
        headerTitle={headerTitle}
        hasTemporaryStorage={hasTemporaryStorage}
        selectAll={selectAll}
        handleSelectAllChange={handleSelectAllChange}
        displayTemporaryStorageAlert={displayTemporaryStorageAlert}
      />

      <div className="bodyContainer">
        <div className="title">
          Herramientas asignadas
        </div>

        {listOfSelectedItems?.length > 0 && (
          <>
            <ProductSelectorList
              items={listOfSelectedItems}
              unselectedItems={listOfUnselectedItems}
              onToggleItem={removeToListToChangeStatus}
              showSerials
              editProductSerial={editProductSerial}
              getErrorText={getErrorText}
              isChecked
              toggleProductSelection={toggleProductSelection}
              selectedProductIds={selectedProductIds}
              hasStockReservation={hasStockReservation}
              isProductSelectionLocked={isProductLockedByStockReservation}
              getStockReservationSerial={getStockReservationSerial}
              isStockOrder={isStockOrder}
              requireSerials={requireSerials}
              countSelectableUnitsOnly={shouldBlockReservedStockSelection}
            />
            {customAlertMessage && (
              <div className="hasTemporaryStorageAlert">
                <div className="iconAlertContainer">
                  <BordOneToneIcon variant="alertTriangleWarning" customWidth="16px" />
                </div>
                <span className="alertMessageText">{customAlertMessage}</span>
              </div>
            )}
          </>
        )}

        {displayDivider && <div className="divider" />}

        {listOfUnselectedItems?.length > 0 && (
          <div className="selectedProducts">
            <div className="title">
              {shipmentNumberForLabel != null && `Otras herramientas del envío ${shipmentNumberForLabel}`}
            </div>
            <ProductSelectorList
              items={listOfUnselectedItems}
              unselectedItems={listOfSelectedItems}
              onToggleItem={addToListToChangeStatus}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductSelector;
